/**
 * Country dialing codes used by the Add/Edit Lead phone input, the lead
 * filter's Country Code picker and the import default-code select.
 *
 * Every geographic ITU-T country calling code, ONE entry per code: pickers
 * key their options by `code`, so territories sharing a code (+1 NANP, +7,
 * +44 Crown Dependencies, +262, +590, +599, …) are folded into a single
 * entry and their other names ride `keywords` for search. Our primary
 * markets are listed first (the order the dropdown opens with); everything
 * else follows alphabetically. Code is the E.164 prefix WITH the leading `+`
 * to match what the server stores in `Leads.phoneCountryCode`.
 */
export interface CountryDialCode {
  /** E.164 prefix, e.g. "+91". */
  code: string;
  /** ISO 3166-1 alpha-2 region, e.g. "IN". Unique across the list. */
  region: string;
  /** Human label shown in the dropdown. */
  label: string;
  /** Extra search-only names (other territories sharing the code, aliases). */
  keywords?: string[];
}

export const COUNTRY_DIAL_CODES: CountryDialCode[] = [
  // ── Primary markets (dropdown opens with these) ──
  { code: "+91", region: "IN", label: "+91 India" },
  { code: "+971", region: "AE", label: "+971 UAE", keywords: ["United Arab Emirates", "Dubai"] },
  {
    code: "+1",
    region: "US",
    label: "+1 USA / Canada",
    keywords: [
      "United States", "America", "Canada", "Puerto Rico", "Jamaica", "Bahamas",
      "Barbados", "Trinidad and Tobago", "Dominican Republic", "Bermuda",
      "Cayman Islands", "Antigua and Barbuda", "Anguilla", "British Virgin Islands",
      "US Virgin Islands", "Dominica", "Grenada", "Guam", "Montserrat",
      "Northern Mariana Islands", "American Samoa", "Saint Kitts and Nevis",
      "Saint Lucia", "Saint Vincent and the Grenadines", "Sint Maarten",
      "Turks and Caicos Islands",
    ],
  },
  {
    code: "+44",
    region: "GB",
    label: "+44 UK",
    keywords: ["United Kingdom", "Britain", "England", "Scotland", "Wales", "Northern Ireland", "Guernsey", "Jersey", "Isle of Man"],
  },
  { code: "+61", region: "AU", label: "+61 Australia", keywords: ["Christmas Island", "Cocos Islands"] },
  { code: "+64", region: "NZ", label: "+64 New Zealand", keywords: ["Pitcairn Islands"] },
  { code: "+65", region: "SG", label: "+65 Singapore" },
  { code: "+60", region: "MY", label: "+60 Malaysia" },
  { code: "+66", region: "TH", label: "+66 Thailand" },
  { code: "+62", region: "ID", label: "+62 Indonesia" },
  { code: "+63", region: "PH", label: "+63 Philippines" },
  { code: "+92", region: "PK", label: "+92 Pakistan" },
  { code: "+94", region: "LK", label: "+94 Sri Lanka" },
  { code: "+880", region: "BD", label: "+880 Bangladesh" },
  { code: "+977", region: "NP", label: "+977 Nepal" },
  { code: "+966", region: "SA", label: "+966 Saudi Arabia" },
  { code: "+974", region: "QA", label: "+974 Qatar" },
  { code: "+965", region: "KW", label: "+965 Kuwait" },
  { code: "+968", region: "OM", label: "+968 Oman" },
  { code: "+973", region: "BH", label: "+973 Bahrain" },
  { code: "+49", region: "DE", label: "+49 Germany" },
  { code: "+33", region: "FR", label: "+33 France" },
  { code: "+34", region: "ES", label: "+34 Spain" },
  { code: "+39", region: "IT", label: "+39 Italy", keywords: ["Vatican City"] },
  { code: "+31", region: "NL", label: "+31 Netherlands" },
  { code: "+46", region: "SE", label: "+46 Sweden" },
  { code: "+27", region: "ZA", label: "+27 South Africa" },
  { code: "+254", region: "KE", label: "+254 Kenya" },
  { code: "+234", region: "NG", label: "+234 Nigeria" },
  { code: "+20", region: "EG", label: "+20 Egypt" },

  // ── Everything else, alphabetical ──
  { code: "+93", region: "AF", label: "+93 Afghanistan" },
  { code: "+355", region: "AL", label: "+355 Albania" },
  { code: "+213", region: "DZ", label: "+213 Algeria" },
  { code: "+376", region: "AD", label: "+376 Andorra" },
  { code: "+244", region: "AO", label: "+244 Angola" },
  { code: "+54", region: "AR", label: "+54 Argentina" },
  { code: "+374", region: "AM", label: "+374 Armenia" },
  { code: "+297", region: "AW", label: "+297 Aruba" },
  { code: "+247", region: "AC", label: "+247 Ascension Island" },
  { code: "+43", region: "AT", label: "+43 Austria" },
  { code: "+994", region: "AZ", label: "+994 Azerbaijan" },
  { code: "+375", region: "BY", label: "+375 Belarus" },
  { code: "+32", region: "BE", label: "+32 Belgium" },
  { code: "+501", region: "BZ", label: "+501 Belize" },
  { code: "+229", region: "BJ", label: "+229 Benin" },
  { code: "+975", region: "BT", label: "+975 Bhutan" },
  { code: "+591", region: "BO", label: "+591 Bolivia" },
  { code: "+387", region: "BA", label: "+387 Bosnia and Herzegovina" },
  { code: "+267", region: "BW", label: "+267 Botswana" },
  { code: "+55", region: "BR", label: "+55 Brazil" },
  { code: "+246", region: "IO", label: "+246 British Indian Ocean Territory", keywords: ["Diego Garcia"] },
  { code: "+673", region: "BN", label: "+673 Brunei" },
  { code: "+359", region: "BG", label: "+359 Bulgaria" },
  { code: "+226", region: "BF", label: "+226 Burkina Faso" },
  { code: "+257", region: "BI", label: "+257 Burundi" },
  { code: "+855", region: "KH", label: "+855 Cambodia" },
  { code: "+237", region: "CM", label: "+237 Cameroon" },
  { code: "+238", region: "CV", label: "+238 Cape Verde", keywords: ["Cabo Verde"] },
  { code: "+236", region: "CF", label: "+236 Central African Republic" },
  { code: "+235", region: "TD", label: "+235 Chad" },
  { code: "+56", region: "CL", label: "+56 Chile" },
  { code: "+86", region: "CN", label: "+86 China" },
  { code: "+57", region: "CO", label: "+57 Colombia" },
  { code: "+269", region: "KM", label: "+269 Comoros" },
  { code: "+242", region: "CG", label: "+242 Congo", keywords: ["Republic of the Congo", "Brazzaville"] },
  { code: "+682", region: "CK", label: "+682 Cook Islands" },
  { code: "+506", region: "CR", label: "+506 Costa Rica" },
  { code: "+225", region: "CI", label: "+225 Côte d'Ivoire", keywords: ["Ivory Coast", "Cote d'Ivoire"] },
  { code: "+385", region: "HR", label: "+385 Croatia" },
  { code: "+53", region: "CU", label: "+53 Cuba" },
  { code: "+599", region: "CW", label: "+599 Curaçao / Caribbean Netherlands", keywords: ["Curacao", "Bonaire", "Sint Eustatius", "Saba"] },
  { code: "+357", region: "CY", label: "+357 Cyprus" },
  { code: "+420", region: "CZ", label: "+420 Czech Republic", keywords: ["Czechia"] },
  { code: "+243", region: "CD", label: "+243 DR Congo", keywords: ["Democratic Republic of the Congo", "Kinshasa"] },
  { code: "+45", region: "DK", label: "+45 Denmark" },
  { code: "+253", region: "DJ", label: "+253 Djibouti" },
  { code: "+593", region: "EC", label: "+593 Ecuador" },
  { code: "+503", region: "SV", label: "+503 El Salvador" },
  { code: "+240", region: "GQ", label: "+240 Equatorial Guinea" },
  { code: "+291", region: "ER", label: "+291 Eritrea" },
  { code: "+372", region: "EE", label: "+372 Estonia" },
  { code: "+268", region: "SZ", label: "+268 Eswatini", keywords: ["Swaziland"] },
  { code: "+251", region: "ET", label: "+251 Ethiopia" },
  { code: "+500", region: "FK", label: "+500 Falkland Islands" },
  { code: "+298", region: "FO", label: "+298 Faroe Islands" },
  { code: "+679", region: "FJ", label: "+679 Fiji" },
  { code: "+358", region: "FI", label: "+358 Finland", keywords: ["Åland Islands", "Aland Islands"] },
  { code: "+594", region: "GF", label: "+594 French Guiana" },
  { code: "+689", region: "PF", label: "+689 French Polynesia", keywords: ["Tahiti"] },
  { code: "+241", region: "GA", label: "+241 Gabon" },
  { code: "+220", region: "GM", label: "+220 Gambia" },
  { code: "+995", region: "GE", label: "+995 Georgia" },
  { code: "+233", region: "GH", label: "+233 Ghana" },
  { code: "+350", region: "GI", label: "+350 Gibraltar" },
  { code: "+30", region: "GR", label: "+30 Greece" },
  { code: "+299", region: "GL", label: "+299 Greenland" },
  { code: "+590", region: "GP", label: "+590 Guadeloupe", keywords: ["Saint Barthélemy", "Saint Martin"] },
  { code: "+502", region: "GT", label: "+502 Guatemala" },
  { code: "+224", region: "GN", label: "+224 Guinea" },
  { code: "+245", region: "GW", label: "+245 Guinea-Bissau" },
  { code: "+592", region: "GY", label: "+592 Guyana" },
  { code: "+509", region: "HT", label: "+509 Haiti" },
  { code: "+504", region: "HN", label: "+504 Honduras" },
  { code: "+852", region: "HK", label: "+852 Hong Kong" },
  { code: "+36", region: "HU", label: "+36 Hungary" },
  { code: "+354", region: "IS", label: "+354 Iceland" },
  { code: "+98", region: "IR", label: "+98 Iran" },
  { code: "+964", region: "IQ", label: "+964 Iraq" },
  { code: "+353", region: "IE", label: "+353 Ireland" },
  { code: "+972", region: "IL", label: "+972 Israel" },
  { code: "+81", region: "JP", label: "+81 Japan" },
  { code: "+962", region: "JO", label: "+962 Jordan" },
  { code: "+686", region: "KI", label: "+686 Kiribati" },
  { code: "+383", region: "XK", label: "+383 Kosovo" },
  { code: "+996", region: "KG", label: "+996 Kyrgyzstan" },
  { code: "+856", region: "LA", label: "+856 Laos" },
  { code: "+371", region: "LV", label: "+371 Latvia" },
  { code: "+961", region: "LB", label: "+961 Lebanon" },
  { code: "+266", region: "LS", label: "+266 Lesotho" },
  { code: "+231", region: "LR", label: "+231 Liberia" },
  { code: "+218", region: "LY", label: "+218 Libya" },
  { code: "+423", region: "LI", label: "+423 Liechtenstein" },
  { code: "+370", region: "LT", label: "+370 Lithuania" },
  { code: "+352", region: "LU", label: "+352 Luxembourg" },
  { code: "+853", region: "MO", label: "+853 Macau", keywords: ["Macao"] },
  { code: "+261", region: "MG", label: "+261 Madagascar" },
  { code: "+265", region: "MW", label: "+265 Malawi" },
  { code: "+960", region: "MV", label: "+960 Maldives" },
  { code: "+223", region: "ML", label: "+223 Mali" },
  { code: "+356", region: "MT", label: "+356 Malta" },
  { code: "+692", region: "MH", label: "+692 Marshall Islands" },
  { code: "+596", region: "MQ", label: "+596 Martinique" },
  { code: "+222", region: "MR", label: "+222 Mauritania" },
  { code: "+230", region: "MU", label: "+230 Mauritius" },
  { code: "+52", region: "MX", label: "+52 Mexico" },
  { code: "+691", region: "FM", label: "+691 Micronesia" },
  { code: "+373", region: "MD", label: "+373 Moldova" },
  { code: "+377", region: "MC", label: "+377 Monaco" },
  { code: "+976", region: "MN", label: "+976 Mongolia" },
  { code: "+382", region: "ME", label: "+382 Montenegro" },
  { code: "+212", region: "MA", label: "+212 Morocco", keywords: ["Western Sahara"] },
  { code: "+258", region: "MZ", label: "+258 Mozambique" },
  { code: "+95", region: "MM", label: "+95 Myanmar", keywords: ["Burma"] },
  { code: "+264", region: "NA", label: "+264 Namibia" },
  { code: "+674", region: "NR", label: "+674 Nauru" },
  { code: "+687", region: "NC", label: "+687 New Caledonia" },
  { code: "+505", region: "NI", label: "+505 Nicaragua" },
  { code: "+227", region: "NE", label: "+227 Niger" },
  { code: "+683", region: "NU", label: "+683 Niue" },
  { code: "+672", region: "NF", label: "+672 Norfolk Island", keywords: ["Antarctica"] },
  { code: "+850", region: "KP", label: "+850 North Korea" },
  { code: "+389", region: "MK", label: "+389 North Macedonia", keywords: ["Macedonia"] },
  { code: "+47", region: "NO", label: "+47 Norway", keywords: ["Svalbard"] },
  { code: "+680", region: "PW", label: "+680 Palau" },
  { code: "+970", region: "PS", label: "+970 Palestine" },
  { code: "+507", region: "PA", label: "+507 Panama" },
  { code: "+675", region: "PG", label: "+675 Papua New Guinea" },
  { code: "+595", region: "PY", label: "+595 Paraguay" },
  { code: "+51", region: "PE", label: "+51 Peru" },
  { code: "+48", region: "PL", label: "+48 Poland" },
  { code: "+351", region: "PT", label: "+351 Portugal" },
  { code: "+262", region: "RE", label: "+262 Réunion / Mayotte", keywords: ["Reunion"] },
  { code: "+40", region: "RO", label: "+40 Romania" },
  { code: "+7", region: "RU", label: "+7 Russia / Kazakhstan" },
  { code: "+250", region: "RW", label: "+250 Rwanda" },
  { code: "+290", region: "SH", label: "+290 Saint Helena", keywords: ["Tristan da Cunha"] },
  { code: "+508", region: "PM", label: "+508 Saint Pierre and Miquelon" },
  { code: "+685", region: "WS", label: "+685 Samoa" },
  { code: "+378", region: "SM", label: "+378 San Marino" },
  { code: "+239", region: "ST", label: "+239 São Tomé and Príncipe", keywords: ["Sao Tome and Principe"] },
  { code: "+221", region: "SN", label: "+221 Senegal" },
  { code: "+381", region: "RS", label: "+381 Serbia" },
  { code: "+248", region: "SC", label: "+248 Seychelles" },
  { code: "+232", region: "SL", label: "+232 Sierra Leone" },
  { code: "+421", region: "SK", label: "+421 Slovakia" },
  { code: "+386", region: "SI", label: "+386 Slovenia" },
  { code: "+677", region: "SB", label: "+677 Solomon Islands" },
  { code: "+252", region: "SO", label: "+252 Somalia" },
  { code: "+82", region: "KR", label: "+82 South Korea", keywords: ["Korea"] },
  { code: "+211", region: "SS", label: "+211 South Sudan" },
  { code: "+249", region: "SD", label: "+249 Sudan" },
  { code: "+597", region: "SR", label: "+597 Suriname" },
  { code: "+41", region: "CH", label: "+41 Switzerland" },
  { code: "+963", region: "SY", label: "+963 Syria" },
  { code: "+886", region: "TW", label: "+886 Taiwan" },
  { code: "+992", region: "TJ", label: "+992 Tajikistan" },
  { code: "+255", region: "TZ", label: "+255 Tanzania" },
  { code: "+670", region: "TL", label: "+670 Timor-Leste", keywords: ["East Timor"] },
  { code: "+228", region: "TG", label: "+228 Togo" },
  { code: "+690", region: "TK", label: "+690 Tokelau" },
  { code: "+676", region: "TO", label: "+676 Tonga" },
  { code: "+216", region: "TN", label: "+216 Tunisia" },
  { code: "+90", region: "TR", label: "+90 Turkey", keywords: ["Türkiye", "Turkiye"] },
  { code: "+993", region: "TM", label: "+993 Turkmenistan" },
  { code: "+688", region: "TV", label: "+688 Tuvalu" },
  { code: "+256", region: "UG", label: "+256 Uganda" },
  { code: "+380", region: "UA", label: "+380 Ukraine" },
  { code: "+598", region: "UY", label: "+598 Uruguay" },
  { code: "+998", region: "UZ", label: "+998 Uzbekistan" },
  { code: "+678", region: "VU", label: "+678 Vanuatu" },
  { code: "+58", region: "VE", label: "+58 Venezuela" },
  { code: "+84", region: "VN", label: "+84 Vietnam" },
  { code: "+681", region: "WF", label: "+681 Wallis and Futuna" },
  { code: "+967", region: "YE", label: "+967 Yemen" },
  { code: "+260", region: "ZM", label: "+260 Zambia" },
  { code: "+263", region: "ZW", label: "+263 Zimbabwe" },
];

export const DEFAULT_COUNTRY_CODE = "+91";

/**
 * Every ITU-T E.164 country calling code in use (1–3 digits, no `+`), as
 * assigned in the E.164 zone tables — including the non-geographic codes
 * (800 freephone, 808 shared cost, 870 Inmarsat, 878 UPT, 881/882/883
 * global networks, 888 OCHA, 979 premium rate). Codes only: this list exists
 * so an imported "+<cc><national>" string can be SPLIT into its country code
 * by longest-prefix match. It is a superset of the dropdown list above, which
 * omits the non-geographic codes (and the unused Vatican +379 — Vatican
 * numbers dial through Italy's +39).
 *
 * Sorted LONGEST FIRST so `ALL_DIAL_CODES.find((cc) => digits.startsWith(cc))`
 * is a correct longest-prefix match ("+2…" vs "+20"/"+27", "+3…" vs
 * "+30"…"+39" vs "+350"… are real collisions).
 *
 * It is NOT a validity oracle — the server (libphonenumber) decides whether
 * the national part is a real number.
 */
export const ALL_DIAL_CODES: string[] = (() => {
  const codes = [
    // Zone 1 — NANP
    "1",
    // Zone 2 — Africa (plus Aruba, Faroe Islands, Greenland)
    "20", "211", "212", "213", "216", "218", "220", "221", "222", "223",
    "224", "225", "226", "227", "228", "229", "230", "231", "232", "233",
    "234", "235", "236", "237", "238", "239", "240", "241", "242", "243",
    "244", "245", "246", "247", "248", "249", "250", "251", "252", "253",
    "254", "255", "256", "257", "258", "260", "261", "262", "263", "264",
    "265", "266", "267", "268", "269", "27", "290", "291", "297", "298",
    "299",
    // Zones 3–4 — Europe
    "30", "31", "32", "33", "34", "350", "351", "352", "353", "354", "355",
    "356", "357", "358", "359", "36", "370", "371", "372", "373", "374",
    "375", "376", "377", "378", "379", "380", "381", "382", "383", "385",
    "386", "387", "389", "39",
    "40", "41", "420", "421", "423", "43", "44", "45", "46", "47", "48", "49",
    // Zone 5 — Latin America & Caribbean
    "500", "501", "502", "503", "504", "505", "506", "507", "508", "509",
    "51", "52", "53", "54", "55", "56", "57", "58", "590", "591", "592",
    "593", "594", "595", "596", "597", "598", "599",
    // Zone 6 — Southeast Asia & Oceania
    "60", "61", "62", "63", "64", "65", "66", "670", "672", "673", "674",
    "675", "676", "677", "678", "679", "680", "681", "682", "683", "685",
    "686", "687", "688", "689", "690", "691", "692",
    // Zone 7 — Russia & Kazakhstan
    "7",
    // Zone 8 — East Asia & special services
    "800", "808", "81", "82", "84", "850", "852", "853", "855", "856", "86",
    "870", "878", "880", "881", "882", "883", "886", "888",
    // Zone 9 — West, Central & South Asia, Middle East
    "90", "91", "92", "93", "94", "95", "960", "961", "962", "963", "964",
    "965", "966", "967", "968", "970", "971", "972", "973", "974", "975",
    "976", "977", "979", "98", "992", "993", "994", "995", "996", "998",
  ];
  return Array.from(new Set(codes)).sort(
    (a, b) => b.length - a.length || a.localeCompare(b),
  );
})();

/**
 * Normalises a stored lead phone into the bare digit string that dial /
 * WhatsApp links expect: country code first, no `+`, spaces or punctuation.
 *
 * This is the single source of truth for building `tel:` and `wa.me` links.
 * wa.me in particular is strict — it rejects anything that isn't digits
 * (a leading `+` yields "Phone number shared via url is invalid") and it
 * cannot infer a missing country code, so a bare national number silently
 * fails to open a chat.
 *
 * Returns "" when there's nothing dialable (blank, junk, or a result outside
 * E.164's 8–15 digit range), so callers can show "not available" instead of
 * opening a dead chat window.
 */
export function toDialableDigits(
  storedPhone: string | null | undefined,
  storedCountryCode?: string | null,
): string {
  const raw = (storedPhone ?? "").toString().trim();
  if (!raw) return "";

  // A leading "+" means the value is already E.164 — the dial code is baked
  // in, so never prepend another one.
  if (raw.startsWith("+")) return plausible(raw.replace(/[^\d]/g, ""));

  const digits = raw.replace(/[^\d]/g, "");
  if (!digits) return "";

  const cc = (storedCountryCode || DEFAULT_COUNTRY_CODE).replace(/[^\d]/g, "");
  if (!cc) return plausible(digits);

  // Legacy rows sometimes store the dial code inline with no "+". Treat it
  // as already-prefixed only when removing it still leaves a plausible
  // national number (>= 9 digits), so a genuine 10-digit number that merely
  // starts with the dial code (e.g. +91 9197878417) isn't truncated.
  if (digits.startsWith(cc) && digits.length - cc.length >= 9) {
    return plausible(digits);
  }

  // Drop the national trunk prefix ("0"). E.164 never keeps it after the dial
  // code, and spreadsheet imports routinely carry it (e.g. 09876543210 ->
  // +91 9876543210). This also collapses all-zero junk rows to "".
  const national = digits.replace(/^0+/, "");
  if (!national) return "";

  return plausible(`${cc}${national}`);
}

/** E.164 allows 8–15 digits; anything else can't dial, so treat it as absent. */
function plausible(digits: string): string {
  return digits.length >= 8 && digits.length <= 15 ? digits : "";
}

/** `https://wa.me/<digits>` for a lead, or "" when there's no usable number. */
export function buildWhatsAppUrl(
  storedPhone: string | null | undefined,
  storedCountryCode?: string | null,
): string {
  const digits = toDialableDigits(storedPhone, storedCountryCode);
  return digits ? `https://wa.me/${digits}` : "";
}

/** `tel:+<digits>` for a lead, or "" when there's no usable number. */
export function buildTelHref(
  storedPhone: string | null | undefined,
  storedCountryCode?: string | null,
): string {
  const digits = toDialableDigits(storedPhone, storedCountryCode);
  return digits ? `tel:+${digits}` : "";
}

/**
 * Splits a stored value (which can be either a national-only number or a
 * full E.164 string with `+CC` prefix) into `{ countryCode, national }`.
 * Used by the edit-lead flow to hydrate the form when the server hasn't
 * been backfilled yet.
 */
export function splitStoredPhone(
  storedPhone: string | null | undefined,
  storedCountryCode: string | null | undefined,
): { countryCode: string; national: string } {
  const phone = (storedPhone ?? "").toString().trim();
  if (storedCountryCode && phone) {
    return { countryCode: storedCountryCode, national: phone.replace(/[^\d]/g, "") };
  }
  if (phone.startsWith("+")) {
    // Best-effort: greedy match the longest known prefix.
    const sortedByLength = [...COUNTRY_DIAL_CODES].sort(
      (a, b) => b.code.length - a.code.length,
    );
    for (const c of sortedByLength) {
      if (phone.startsWith(c.code)) {
        return {
          countryCode: c.code,
          national: phone.slice(c.code.length).replace(/[^\d]/g, ""),
        };
      }
    }
    // Unknown prefix — keep the digits and default the dropdown.
    return { countryCode: DEFAULT_COUNTRY_CODE, national: phone.replace(/[^\d]/g, "") };
  }
  return { countryCode: DEFAULT_COUNTRY_CODE, national: phone.replace(/[^\d]/g, "") };
}
